import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/theme-provider';
import { Badge } from '@/components/ui/badge';
import { Sun } from 'lucide-react';
import { useAuthStore, type User } from '@/lib/stores/auth';
import { useNotificationsStore } from '@/lib/stores/notifications';
import { useSidebar } from '@/components/ui/sidebar';
import { getAllowedNavigation, USER_ACCOUNT_ITEMS, ROLE_NAMES, type RoleName } from '@/lib/utils/role-access';
import { getIconComponent } from '@/lib/icons';

// Use the centralized icon component getter

// Navigation item interface
interface NavigationItem {
  title: string;
  url: string;
  icon: React.ComponentType<Record<string, unknown>>;
  badge?: string | number;
}

// Helper function to safely extract role name from user object
const getUserRoleName = (user: User | null): RoleName | undefined => {
  const roleName = user?.role?.name;
  if (!roleName || typeof roleName !== 'string') return undefined;

  // Check if it's a valid role name
  return Object.values(ROLE_NAMES).includes(roleName as RoleName)
    ? roleName as RoleName
    : undefined;
};

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronUp, LogOut, Check, ChevronDown } from 'lucide-react';

// Navigation items are now dynamically generated based on user role
// See lib/utils/role-access.ts for the role-based navigation definitions

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { unreadCount } = useNotificationsStore();
  const { theme, setTheme } = useTheme();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNavigation = (href: string) => {
    // Close mobile sidebar when navigating
    if (isMobile) {
      setOpenMobile(false);
    }
    navigate(href);
  };

  // Handle hydration - unreadCount may be undefined initially
  const safeUnreadCount = typeof unreadCount === 'number' ? unreadCount : 0;
  const userRole = getUserRoleName(user); // Safely extract role name with type checking

  // Get role-based navigation items
  const allowedNavigation = getAllowedNavigation(userRole);

  // Group navigation items into hierarchical categories
  const groupedNavigation = {
    "Content & Streaming": [] as NavigationItem[],
    "Community": [] as NavigationItem[],
    "Administration": [] as NavigationItem[],
  };

  // Categorize each navigation item
  allowedNavigation.forEach(item => {
    const processedItem = {
      title: item.name,
      url: item.href,
      icon: getIconComponent(item.icon),
      badge: item.badge === 'unread' && safeUnreadCount > 0 ? safeUnreadCount : undefined,
    } as NavigationItem;

    // Content & Streaming - Maintain strict order: Dashboard, Streams, Media, then others
    if (item.name === 'Dashboard') {
      groupedNavigation["Content & Streaming"].push(processedItem);
    } else if (item.name === 'Streams') {
      groupedNavigation["Content & Streaming"].push(processedItem);
    } else if (item.name === 'Media') {
      groupedNavigation["Content & Streaming"].push(processedItem);
    } else if (['Create Stream', 'Manage Streams', 'Recordings'].includes(item.name)) {
      groupedNavigation["Content & Streaming"].push(processedItem);
    }
    // Community
    else if (['Comments', 'Notifications', 'Pastor Dashboard'].includes(item.name)) {
      groupedNavigation["Community"].push(processedItem);
    }
    // Administration
    else if (['Admin Dashboard', 'User Management', 'Audit Logs', 'Live Inputs', 'Access Requests', 'Webhooks', 'Moderator Dashboard', 'Hero Manager'].includes(item.name)) {
      groupedNavigation["Administration"].push(processedItem);
    }
    // Default to Content & Streaming if not categorized
    else {
      groupedNavigation["Content & Streaming"].push(processedItem);
    }
  });

  const data = {
    user: {
      name: user?.name || "User",
      email: user?.email || "",
      avatar: user?.profilePictureUrl || "",
    },
    navMain: [
      {
        title: "Content & Streaming",
        items: groupedNavigation["Content & Streaming"],
      },
      {
        title: "Community",
        items: groupedNavigation["Community"],
      },
      {
        title: "Administration",
        items: groupedNavigation["Administration"],
      },
      {
        title: "Account",
        items: USER_ACCOUNT_ITEMS.map(item => ({
          title: item.name,
          url: item.href,
          icon: getIconComponent(item.icon),
        } as NavigationItem)),
      },
    ].filter(group => group.items.length > 0), // Only show groups that have items
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="rounded-xl border border-transparent transition-colors hover:border-sidebar-border hover:bg-sidebar-accent/70"
              onClick={() => handleNavigation('/dashboard')}
            >
              <div className="relative aspect-square size-8 rounded-lg bg-sidebar-primary/10 p-1">
                <img
                  src="/logo.png"
                  alt="Dominion Mandate logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]/sidebar:hidden">
                <span className="truncate font-semibold">Dominion Mandate</span>
                <span className="truncate text-xs">Streaming Platform</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {data.navMain.map((group) => (
          <Collapsible key={group.title} asChild defaultOpen={true}>
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger>
                  {group.title}
                  <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          isActive={location.pathname === item.url}
                          tooltip={item.title}
                          className="rounded-xl data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground"
                          onClick={() => handleNavigation(item.url)}
                        >
                          {item.icon && <item.icon />}
                          <span>{item.title}</span>
                          {item.badge && (
                            <Badge variant="destructive" className="ml-auto text-xs">
                              {typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}
                            </Badge>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton>
                  <Sun className="size-4" />
                  <span>Theme</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-40">
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun className="mr-2 size-4" />
                  Light
                  <Check className="ml-auto size-4" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarSeparator />
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="rounded-xl border border-transparent data-[state=open]:border-sidebar-border data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <span className="text-sm font-medium">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{data.user.name}</span>
                    <span className="truncate text-xs capitalize">
                      {user?.role?.name?.replace('_', ' ') || 'Member'}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                sideOffset={4}
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              >
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar >
  );
}
